import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import configAxiosInterceptors from 'config/axios/interceptors';
import { loadAppScaffold } from 'store/modules/appScaffold/actions';
import { logout as logoutAction } from 'store/modules/auth/actions';
import { loadMyUser, mergeMyUser } from 'store/modules/myUser/actions';
import { setNetworkReady } from 'store/modules/network/actions';
import { postToNative } from 'utils/native-container/bridge';

export const USER_ID_URL = 'https://myfarmbot.com/userId';

interface Props {
  children: JSX.Element;
}

/**
 * A component for Auth0, axios initialisation, and init API calls.
 */
const Auth0TokenHandler = ({ children }: Props) => {
  const dispatch = useDispatch();
  const { getAccessTokenSilently, user: auth0User, loginWithRedirect } = useAuth0();
  const previousAuth0SubRef = useRef<string | undefined>();
  // The last user id announced to the container. auth0User's identity changes
  // on silent token refresh, so without this the effect re-announces the same
  // user through a session and the app re-requests its push token each time.
  const announcedUserIdRef = useRef<number | undefined>();

  useEffect(() => {
    configAxiosInterceptors(getAccessTokenSilently, loginWithRedirect);
  }, [getAccessTokenSilently, loginWithRedirect]);

  useEffect(() => {
    if (!auth0User) {
      // Only for a user we had actually seen. This effect also runs before
      // Auth0 resolves on an ordinary page load, and the container reacts to a
      // sign-out by re-locking and dropping the tapped notification (FR-13).
      if (previousAuth0SubRef.current) {
        postToNative({ type: 'USER_SIGNED_OUT' });
      }
      previousAuth0SubRef.current = undefined;
      announcedUserIdRef.current = undefined;
      return;
    }

    const userId = +auth0User[USER_ID_URL];
    if (!userId) return;

    const auth0Sub = auth0User.sub;
    if (previousAuth0SubRef.current && previousAuth0SubRef.current !== auth0Sub) {
      // A different account on the same session. No USER_SIGNED_OUT is posted
      // here and none is needed: the container treats USER_IDENTIFIED as an
      // exclusive rebind, deregistering the previous user's token before
      // registering the new one (see onUserIdentified in the app's
      // deviceSession). Posting both would make it deregister twice.
      dispatch(logoutAction()); // Reset Redux state.
    }
    previousAuth0SubRef.current = auth0Sub;

    dispatch(setNetworkReady(true));
    dispatch(mergeMyUser({ id: userId }));
    // Once per user, not per effect run (FR-29). A no-op in a browser.
    //
    // The ref is set only once the message has actually left. postToNative
    // returns false when the container has not injected its bridge yet, which
    // it may not have on first render; committing the ref regardless would
    // mark the user announced and never retry, so the container would never
    // learn who is signed in and would register no push token at all.
    if (announcedUserIdRef.current !== userId) {
      if (postToNative({ type: 'USER_IDENTIFIED', userId })) {
        announcedUserIdRef.current = userId;
      }
    }
    dispatch(loadMyUser()); // Load user profile.
    dispatch(loadAppScaffold()); // Load Core API.
  }, [auth0User, dispatch]);

  return children;
};

export default Auth0TokenHandler;
